<template>
  <div class="container mt-4">
    <div class="d-flex align-items-center mb-4">
      <h2 class="mb-0"><i class="bi bi-shield-lock me-2"></i>Panel de Administración</h2>
    </div>

    <ul class="nav nav-tabs mb-4" id="adminTabs">
      <li class="nav-item">
        <button
          class="nav-link"
          :class="{ active: tabActiva === 'invitar' }"
          @click="tabActiva = 'invitar'"
        >
          <i class="bi bi-envelope-plus me-1"></i> Invitar Usuario
        </button>
      </li>
      <li class="nav-item">
        <button
          class="nav-link"
          :class="{ active: tabActiva === 'usuarios' }"
          @click="tabActiva = 'usuarios'; cargarUsuarios()"
        >
          <i class="bi bi-people me-1"></i> Usuarios
        </button>
      </li>
      <li class="nav-item">
        <button
          class="nav-link"
          :class="{ active: tabActiva === 'categorias' }"
          @click="tabActiva = 'categorias'; cargarCategoriasDefault()"
        >
          <i class="bi bi-tags me-1"></i> Categorías por defecto
        </button>
      </li>
    </ul>

    <!-- Tab: Invitar usuario -->
    <div v-if="tabActiva === 'invitar'" class="card shadow-sm">
      <div class="card-header bg-success text-white">
        <h5 class="mb-0"><i class="bi bi-envelope-plus me-2"></i>Invitar nuevo usuario</h5>
      </div>
      <div class="card-body">
        <p class="text-muted">El usuario recibirá un email con un enlace para crear su cuenta. El enlace es válido durante 7 días.</p>

        <div v-if="invitacionExito" class="alert alert-success alert-dismissible">
          <i class="bi bi-check-circle me-2"></i>{{ invitacionExito }}
          <button type="button" class="btn-close" @click="invitacionExito = ''"></button>
        </div>

        <div v-if="invitacionError" class="alert alert-danger alert-dismissible">
          {{ invitacionError }}
          <button type="button" class="btn-close" @click="invitacionError = ''"></button>
        </div>

        <form @submit.prevent="enviarInvitacion" class="row g-3">
          <div class="col-md-8">
            <label class="form-label">Email del nuevo usuario</label>
            <input
              type="email"
              class="form-control"
              v-model="emailInvitacion"
              placeholder="nuevo@email.com"
              required
            />
          </div>
          <div class="col-md-4 d-flex align-items-end">
            <button type="submit" class="btn btn-success w-100" :disabled="enviandoInvitacion">
              <span v-if="enviandoInvitacion" class="spinner-border spinner-border-sm me-2"></span>
              {{ enviandoInvitacion ? 'Enviando...' : 'Enviar invitación' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Tab: Usuarios -->
    <div v-if="tabActiva === 'usuarios'" class="card shadow-sm">
      <div class="card-header bg-primary text-white">
        <h5 class="mb-0"><i class="bi bi-people me-2"></i>Usuarios registrados</h5>
      </div>
      <div class="card-body">
        <div v-if="limpiezaExito" class="alert alert-success alert-dismissible">
          <i class="bi bi-check-circle me-2"></i>{{ limpiezaExito }}
          <button type="button" class="btn-close" @click="limpiezaExito = ''"></button>
        </div>
        <div v-if="limpiezaError" class="alert alert-danger alert-dismissible">
          {{ limpiezaError }}
          <button type="button" class="btn-close" @click="limpiezaError = ''"></button>
        </div>
        <div v-if="cargandoUsuarios" class="text-center py-3">
          <div class="spinner-border text-primary"></div>
        </div>
        <div class="table-responsive" v-else>
          <table class="table table-hover table-sm mb-0">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Email</th>
                <th>Rol</th>
                <th class="text-center">Ingresos</th>
                <th class="text-center" title="Muestra el menú de hoy (comida y cena) en la vista Mi día">Hoy como</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="u in usuarios" :key="u.id">
                <td>{{ u.nombre }}</td>
                <td>{{ u.email }}</td>
                <td>
                  <span v-if="u.es_admin" class="badge bg-danger">Admin</span>
                  <span v-else class="badge bg-secondary">Usuario</span>
                </td>
                <td class="text-center">
                  <div class="form-check form-switch d-inline-flex" style="padding-left: 2.5em;">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      :id="`ing-${u.id}`"
                      :checked="!!u.ingresos_habilitado"
                      :disabled="toggleandoIngresos === u.id"
                      @change="toggleIngresos(u)"
                    />
                  </div>
                </td>
                <td class="text-center">
                  <div class="form-check form-switch d-inline-flex" style="padding-left: 2.5em;">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      :id="`nut-${u.id}`"
                      :checked="!!u.mi_dia_nutricion || !!u.es_admin"
                      :disabled="!!u.es_admin || toggleandoNutricion === u.id"
                      @change="toggleNutricion(u)"
                    />
                  </div>
                </td>
                <td>
                  <button
                    v-if="!u.es_admin"
                    class="btn btn-sm btn-outline-danger"
                    :disabled="limpiandoId === u.id"
                    @click="limpiarDatosUsuario(u)"
                    title="Eliminar todos los gastos, suscripciones y planificados de este usuario"
                  >
                    <span v-if="limpiandoId === u.id" class="spinner-border spinner-border-sm me-1"></span>
                    <i v-else class="bi bi-trash me-1"></i>
                    Limpiar datos
                  </button>
                </td>
              </tr>
              <tr v-if="usuarios.length === 0">
                <td colspan="5" class="text-center text-muted">No hay usuarios</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Tab: Categorías por defecto -->
    <div v-if="tabActiva === 'categorias'" class="card shadow-sm">
      <div class="card-header bg-secondary text-white d-flex justify-content-between align-items-center">
        <h5 class="mb-0"><i class="bi bi-tags me-2"></i>Categorías por defecto</h5>
      </div>
      <div class="card-body">
        <p class="text-muted small mb-3">
          Estas categorías se asignan automáticamente a cada nuevo usuario al registrarse. Los usuarios ya registrados mantienen sus propias categorías.
        </p>

        <div v-if="catDefaultExito" class="alert alert-success alert-dismissible py-2">
          <i class="bi bi-check-circle me-1"></i>{{ catDefaultExito }}
          <button type="button" class="btn-close" @click="catDefaultExito = ''"></button>
        </div>
        <div v-if="catDefaultError" class="alert alert-danger alert-dismissible py-2">
          <i class="bi bi-exclamation-circle me-1"></i>{{ catDefaultError }}
          <button type="button" class="btn-close" @click="catDefaultError = ''"></button>
        </div>

        <!-- Formulario para nueva categoría -->
        <form class="d-flex gap-2 mb-3" @submit.prevent="agregarCatDefault">
          <input
            v-model="nuevaCatDefault"
            type="text"
            class="form-control form-control-sm"
            placeholder="Nueva categoría por defecto..."
            :disabled="guardandoCatDefault"
          />
          <button class="btn btn-success btn-sm px-3" type="submit" :disabled="!nuevaCatDefault.trim() || guardandoCatDefault">
            <i class="bi bi-plus-lg"></i> Añadir
          </button>
        </form>

        <!-- Lista de categorías por defecto -->
        <div v-if="cargandoCatDefault" class="text-center py-3">
          <div class="spinner-border spinner-border-sm text-secondary"></div>
        </div>
        <ul v-else class="list-group list-group-flush">
          <li
            v-for="cat in categoriasDefault"
            :key="cat.id"
            class="list-group-item d-flex align-items-center gap-2 px-0"
          >
            <template v-if="editandoCatId === cat.id">
              <input
                v-model="editandoCatNombre"
                type="text"
                class="form-control form-control-sm flex-grow-1"
                @keyup.enter="guardarEdicionCat(cat)"
                @keyup.escape="editandoCatId = null"
              />
              <button class="btn btn-sm btn-primary" @click="guardarEdicionCat(cat)" :disabled="!editandoCatNombre.trim()">
                <i class="bi bi-check-lg"></i>
              </button>
              <button class="btn btn-sm btn-outline-secondary" @click="editandoCatId = null">
                <i class="bi bi-x-lg"></i>
              </button>
            </template>
            <template v-else>
              <span class="flex-grow-1">{{ cat.nombre }}</span>
              <button class="btn btn-sm btn-outline-primary" @click="iniciarEdicionCat(cat)" title="Editar">
                <i class="bi bi-pencil"></i>
              </button>
              <button class="btn btn-sm btn-outline-danger" @click="eliminarCatDefault(cat)" title="Eliminar">
                <i class="bi bi-trash"></i>
              </button>
            </template>
          </li>
          <li v-if="!categoriasDefault.length" class="list-group-item text-muted text-center px-0">
            No hay categorías por defecto definidas.
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import axios from '../config/axios';

export default {
  name: 'AdminView',
  setup() {
    const tabActiva = ref('invitar');
    const emailInvitacion = ref('');
    const enviandoInvitacion = ref(false);
    const invitacionExito = ref('');
    const invitacionError = ref('');
    const usuarios = ref([]);
    const cargandoUsuarios = ref(false);
    const limpiandoId = ref(null);
    const limpiezaExito = ref('');
    const limpiezaError = ref('');

    // Categorías por defecto
    const categoriasDefault = ref([]);
    const cargandoCatDefault = ref(false);
    const nuevaCatDefault = ref('');
    const guardandoCatDefault = ref(false);
    const catDefaultExito = ref('');
    const catDefaultError = ref('');
    const editandoCatId = ref(null);
    const editandoCatNombre = ref('');

    const enviarInvitacion = async () => {
      enviandoInvitacion.value = true;
      invitacionExito.value = '';
      invitacionError.value = '';
      try {
        const res = await axios.post('/api/admin/invitar', { email: emailInvitacion.value });
        invitacionExito.value = res.data.message;
        emailInvitacion.value = '';
      } catch (err) {
        invitacionError.value = err.response?.data?.error || 'Error al enviar la invitación';
      } finally {
        enviandoInvitacion.value = false;
      }
    };

    const cargarUsuarios = async () => {
      cargandoUsuarios.value = true;
      try {
        const res = await axios.get('/api/admin/usuarios');
        usuarios.value = res.data;
      } catch (err) {
        console.error('Error al cargar usuarios:', err);
      } finally {
        cargandoUsuarios.value = false;
      }
    };

    const toggleandoIngresos = ref(null);
    const toggleIngresos = async (u) => {
      toggleandoIngresos.value = u.id;
      try {
        const res = await axios.put(`/api/admin/usuarios/${u.id}/toggle-ingresos`);
        u.ingresos_habilitado = res.data.ingresos_habilitado;
      } catch (err) {
        limpiezaError.value = err.response?.data?.error || 'Error al cambiar el módulo de ingresos';
      } finally {
        toggleandoIngresos.value = null;
      }
    };

    const toggleandoNutricion = ref(null);
    const toggleNutricion = async (u) => {
      toggleandoNutricion.value = u.id;
      try {
        const res = await axios.put(`/api/admin/usuarios/${u.id}/toggle-mi-dia-nutricion`);
        u.mi_dia_nutricion = res.data.mi_dia_nutricion;
      } catch (err) {
        limpiezaError.value = err.response?.data?.error || 'Error al cambiar la tarjeta Hoy como';
      } finally {
        toggleandoNutricion.value = null;
      }
    };

    const limpiarDatosUsuario = async (u) => {
      if (!confirm(`¿Seguro que quieres eliminar TODOS los gastos, suscripciones y planificados de ${u.nombre}? Esta acción no se puede deshacer.`)) return;
      limpiezaExito.value = '';
      limpiezaError.value = '';
      limpiandoId.value = u.id;
      try {
        const res = await axios.delete(`/api/admin/usuarios/${u.id}/datos`);
        limpiezaExito.value = `${u.nombre}: ${res.data.message}`;
      } catch (err) {
        limpiezaError.value = err.response?.data?.error || 'Error al limpiar los datos del usuario';
      } finally {
        limpiandoId.value = null;
      }
    };

    const cargarCategoriasDefault = async () => {
      cargandoCatDefault.value = true;
      try {
        const res = await axios.get('/api/admin/categorias-default');
        categoriasDefault.value = res.data;
      } catch (err) {
        catDefaultError.value = err.response?.data?.error || 'Error al cargar categorías';
      } finally {
        cargandoCatDefault.value = false;
      }
    };

    const agregarCatDefault = async () => {
      const nombre = nuevaCatDefault.value.trim();
      if (!nombre) return;
      guardandoCatDefault.value = true;
      catDefaultExito.value = '';
      catDefaultError.value = '';
      try {
        const res = await axios.post('/api/admin/categorias-default', { nombre });
        categoriasDefault.value.push(res.data);
        categoriasDefault.value.sort((a, b) => a.nombre.localeCompare(b.nombre));
        nuevaCatDefault.value = '';
        catDefaultExito.value = `Categoría "${nombre}" añadida`;
      } catch (err) {
        catDefaultError.value = err.response?.data?.error || 'Error al añadir categoría';
      } finally {
        guardandoCatDefault.value = false;
      }
    };

    const iniciarEdicionCat = (cat) => {
      editandoCatId.value = cat.id;
      editandoCatNombre.value = cat.nombre;
    };

    const guardarEdicionCat = async (cat) => {
      const nombre = editandoCatNombre.value.trim();
      if (!nombre) return;
      catDefaultExito.value = '';
      catDefaultError.value = '';
      try {
        await axios.put(`/api/admin/categorias-default/${cat.id}`, { nombre });
        const idx = categoriasDefault.value.findIndex(c => c.id === cat.id);
        if (idx !== -1) categoriasDefault.value[idx].nombre = nombre;
        categoriasDefault.value.sort((a, b) => a.nombre.localeCompare(b.nombre));
        editandoCatId.value = null;
        catDefaultExito.value = `Categoría actualizada a "${nombre}"`;
      } catch (err) {
        catDefaultError.value = err.response?.data?.error || 'Error al actualizar categoría';
      }
    };

    const eliminarCatDefault = async (cat) => {
      if (!confirm(`¿Eliminar la categoría por defecto "${cat.nombre}"?`)) return;
      catDefaultExito.value = '';
      catDefaultError.value = '';
      try {
        await axios.delete(`/api/admin/categorias-default/${cat.id}`);
        categoriasDefault.value = categoriasDefault.value.filter(c => c.id !== cat.id);
        catDefaultExito.value = `Categoría "${cat.nombre}" eliminada`;
      } catch (err) {
        catDefaultError.value = err.response?.data?.error || 'Error al eliminar categoría';
      }
    };

    return {
      tabActiva,
      emailInvitacion,
      enviandoInvitacion,
      invitacionExito,
      invitacionError,
      usuarios,
      cargandoUsuarios,
      limpiandoId,
      limpiezaExito,
      limpiezaError,
      enviarInvitacion,
      cargarUsuarios,
      limpiarDatosUsuario,
      toggleandoIngresos,
      toggleIngresos,
      toggleandoNutricion,
      toggleNutricion,
      categoriasDefault,
      cargandoCatDefault,
      nuevaCatDefault,
      guardandoCatDefault,
      catDefaultExito,
      catDefaultError,
      editandoCatId,
      editandoCatNombre,
      cargarCategoriasDefault,
      agregarCatDefault,
      iniciarEdicionCat,
      guardarEdicionCat,
      eliminarCatDefault
    };
  }
};
</script>
